import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cancelOrder, placeEquityOrder } from '../../api/trading'
import { placeOptionOrder } from '../../api/options'
import { ApiError } from '../../api/http'
import { useSetting } from '../../hooks/useSetting'
import { resolveLimitPrice, roundToCent, type OrderTypeMode } from '../../lib/limitPrice'
import { planEquityOrder } from '../../lib/equityOrderPlan'
import { getSessionPhase } from '../../lib/marketHours'
import { useFlatten } from '../../hooks/useFlatten'
import { useMaxLossLimit } from '../../hooks/useMaxLossLimit'
import { toast } from '../../toast/useToastStore'
import { useMaskMoney } from '../../privacy/usePrivacyStore'
import type { ActiveClosedPosition, ActivePendingOrder, ActivePosition } from '../../lib/activeTrades'
import { groupSameSecondFills } from '../../lib/activeTrades'
import type { Transaction } from '../../api/types'
import { PositionPriceChart } from '../PositionPriceChart'
import { AccountChip, useIsAggregateScope } from '../AccountChip'
import { TradeLink } from '../TradeLink'

const ARM_TIMEOUT_MS = 3000

function rawFmtMoney(v: number | string | undefined | null): string {
  if (v == null || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function fmtSignedMoney(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  const formatted = Math.abs(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  if (n > 0) return `+${formatted}`
  if (n < 0) return `−${formatted}`
  return formatted
}

function fmtSignedPct(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  const sign = n > 0 ? '+' : n < 0 ? '−' : ''
  return `${sign}${Math.abs(n).toFixed(2)}%`
}

function fmtVolume(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function fmtTimeOfDay(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function elapsedSince(iso: string | undefined): string {
  if (!iso) return ''
  const ms = Date.now() - Date.parse(iso)
  if (!Number.isFinite(ms) || ms < 0) return ''
  const s = Math.floor(ms / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  return `${h}h ago`
}

function useArm(): {
  armed: boolean
  arm: () => void
  clear: () => void
} {
  const [armed, setArmed] = useState(false)
  const timerRef = useRef<number | null>(null)
  function clear() {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setArmed(false)
  }
  function arm() {
    setArmed(true)
    if (timerRef.current != null) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null
      setArmed(false)
    }, ARM_TIMEOUT_MS)
  }
  useEffect(() => () => clear(), [])
  return { armed, arm, clear }
}

interface PositionCardProps {
  row: ActivePosition
  liveThrottled: boolean
}

export function PositionCard({ row, liveThrottled }: PositionCardProps) {
  const qc = useQueryClient()
  const mask = useMaskMoney()
  const isAggregate = useIsAggregateScope()
  const fmtMoney = (v: number | string | undefined | null) => mask(rawFmtMoney(v))

  const { position, livePrice, unrealizedPL, unrealizedPLPct, dayPct, quote, transactions } = row
  const qty = Number(position.quantity)
  const isLong = Number.isFinite(qty) && qty > 0
  const isOption = position.instrument.type === 'OPTION'

  const [quickEnabled] = useSetting<boolean>('quick-trade-enabled', true)
  const [orderMode] = useSetting<OrderTypeMode>('order-type-mode', 'MARKET')
  const maxLoss = useMaxLossLimit()

  const sellArm = useArm()
  const addArm = useArm()
  const [addQty, setAddQty] = useState('')
  const flattenHook = useFlatten({ symbol: position.instrument.symbol, isOption })
  const fadeArms = {
    25: useArm(),
    50: useArm(),
    75: useArm(),
  } as const

  const [submittingLabel, setSubmittingLabel] = useState<string | null>(null)

  const placeSell = useMutation({
    mutationFn: async (sellQty: number) => {
      const last = livePrice ?? Number(position.lastPrice?.lastPrice ?? '')
      if (isOption) {
        let limitPrice: number | undefined
        if (orderMode === 'LIMIT') {
          const px = resolveLimitPrice({ side: 'SELL', last, bid: quote?.bid, ask: quote?.ask })
          if (px == null) throw new Error('No live price for limit order')
          limitPrice = roundToCent(px)
        }
        return placeOptionOrder({
          instrument: { symbol: position.instrument.symbol, type: 'OPTION' },
          side: 'SELL',
          contracts: sellQty,
          openCloseIndicator: 'CLOSE',
          mode: orderMode,
          limitPrice,
        })
      }
      const plan = planEquityOrder({
        phase: getSessionPhase(),
        side: 'SELL',
        bid: quote?.bid,
        ask: quote?.ask,
        last,
      })
      if (plan == null) throw new Error('No live price for extended-hours limit order')
      return placeEquityOrder({
        symbol: position.instrument.symbol,
        side: 'SELL',
        quantity: sellQty,
        mode: plan.mode,
        limitPrice: plan.limitPrice,
        extendedHours: plan.extendedHours,
      })
    },
    onSuccess: (resp, sellQty) => {
      setSubmittingLabel(null)
      toast.success(
        `${position.instrument.symbol} · Sell ${sellQty} submitted · ${resp.orderId}`,
      )
      qc.invalidateQueries({ queryKey: ['portfolio'] })
      qc.invalidateQueries({ queryKey: ['app-orders'] })
      qc.invalidateQueries({ queryKey: ['tx'] })
      qc.invalidateQueries({ queryKey: ['ticker-fills'] })
      // Broker takes a few seconds to surface the new fill in its transaction
      // feed. Re-poll a couple of times so the closing tx appears without a
      // manual refresh.
      for (const delay of [1500, 4000, 8000]) {
        window.setTimeout(() => {
          qc.invalidateQueries({ queryKey: ['portfolio'] })
          qc.invalidateQueries({ queryKey: ['tx'] })
          qc.invalidateQueries({ queryKey: ['ticker-fills'] })
        }, delay)
      }
    },
    onError: (err: unknown) => {
      setSubmittingLabel(null)
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`Sell ${position.instrument.symbol} failed — ${msg}`)
    },
  })

  const placeBuy = useMutation({
    mutationFn: async (buyQty: number) => {
      const last = livePrice ?? Number(position.lastPrice?.lastPrice ?? '')
      if (isOption) {
        let limitPrice: number | undefined
        if (orderMode === 'LIMIT') {
          const px = resolveLimitPrice({ side: 'BUY', last, bid: quote?.bid, ask: quote?.ask })
          if (px == null) throw new Error('No live price for limit order')
          limitPrice = roundToCent(px)
        }
        return placeOptionOrder({
          instrument: { symbol: position.instrument.symbol, type: 'OPTION' },
          side: 'BUY',
          contracts: buyQty,
          openCloseIndicator: 'OPEN',
          mode: orderMode,
          limitPrice,
        })
      }
      const plan = planEquityOrder({
        phase: getSessionPhase(),
        side: 'BUY',
        bid: quote?.bid,
        ask: quote?.ask,
        last,
      })
      if (plan == null) throw new Error('No live price for extended-hours limit order')
      return placeEquityOrder({
        symbol: position.instrument.symbol,
        side: 'BUY',
        quantity: buyQty,
        mode: plan.mode,
        limitPrice: plan.limitPrice,
        extendedHours: plan.extendedHours,
      })
    },
    onSuccess: (resp, buyQty) => {
      setSubmittingLabel(null)
      setAddQty('')
      toast.success(
        `${position.instrument.symbol} · Add ${buyQty} submitted · ${resp.orderId}`,
      )
      qc.invalidateQueries({ queryKey: ['portfolio'] })
      qc.invalidateQueries({ queryKey: ['app-orders'] })
      qc.invalidateQueries({ queryKey: ['tx'] })
      qc.invalidateQueries({ queryKey: ['ticker-fills'] })
      for (const delay of [1500, 4000, 8000]) {
        window.setTimeout(() => {
          qc.invalidateQueries({ queryKey: ['portfolio'] })
          qc.invalidateQueries({ queryKey: ['tx'] })
          qc.invalidateQueries({ queryKey: ['ticker-fills'] })
        }, delay)
      }
    },
    onError: (err: unknown) => {
      setSubmittingLabel(null)
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`Add ${position.instrument.symbol} failed — ${msg}`)
    },
  })

  function triggerAdd() {
    if (submittingLabel || flattenHook.isPending) return
    const n = Number(addQty)
    if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
      toast.error(`Enter a whole ${isOption ? 'contract' : 'share'} qty > 0`)
      return
    }
    if (!quickEnabled && !addArm.armed) {
      addArm.arm()
      return
    }
    addArm.clear()
    setSubmittingLabel('Add')
    placeBuy.mutate(n)
  }

  function trigger(label: string, sellQty: number, arm: ReturnType<typeof useArm>) {
    if (submittingLabel) return
    if (sellQty <= 0) return
    if (!quickEnabled && !arm.armed) {
      arm.arm()
      return
    }
    arm.clear()
    setSubmittingLabel(label)
    placeSell.mutate(sellQty)
  }

  function fadeQty(pct: 25 | 50 | 75): number {
    return Math.floor(Math.abs(qty) * (pct / 100))
  }

  const dotColor = liveThrottled ? 'bg-amber-400' : 'bg-emerald-400'
  const dotAnim = liveThrottled ? '' : 'animate-pulse'
  const plPositive = unrealizedPL >= 0
  const plClass = plPositive ? 'text-emerald-400' : 'text-red-400'
  const sideLabel = isLong ? 'LONG' : 'SHORT'
  const sideClass = isLong ? 'text-emerald-400' : 'text-red-400'

  const displayPrice = livePrice ?? Number(position.lastPrice?.lastPrice ?? '')

  const groupedTx = groupSameSecondFills(transactions)

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
      <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor} ${dotAnim}`} />
                <span className="font-mono font-semibold text-zinc-100">
                  {position.instrument.symbol}
                </span>
                <span className={`text-[10px] font-semibold tracking-wide ${sideClass}`}>
                  {sideLabel} · {position.quantity}
                  {isOption ? ' ct' : ''}
                </span>
                {isAggregate && position.accountId && (
                  <AccountChip accountId={position.accountId} />
                )}
                <TradeLink upstreamOrderId={(transactions ?? []).map((t) => t.orderId)} />
              </div>
              <div className="text-[11px] text-zinc-500 mt-1 truncate">
                opened {fmtTimeOfDay(position.openedAt)} · cost{' '}
                {fmtMoney(position.costBasis?.unitCost)}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono text-xl text-zinc-100">
                {Number.isFinite(displayPrice) ? fmtMoney(displayPrice) : '—'}
              </div>
              <div className={`font-mono text-sm ${plClass}`}>
                {fmtSignedMoney(unrealizedPL)}{' '}
                <span className="text-xs">({fmtSignedPct(unrealizedPLPct)})</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-zinc-500">
            <span>
              Day <span className={dayPct >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                {fmtSignedPct(dayPct)}
              </span>
            </span>
            <span>Prev {fmtMoney(quote?.previousClose)}</span>
            <span>Vol {fmtVolume(quote?.volume)}</span>
          </div>

          {isLong ? (
            <>
              <div className="flex gap-1.5">
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={addQty}
                  onChange={(e) => {
                    setAddQty(e.target.value)
                    addArm.clear()
                  }}
                  placeholder={isOption ? 'ct' : 'sh'}
                  disabled={submittingLabel != null || flattenHook.isPending || maxLoss.capReached}
                  aria-label={`Quantity to add to ${position.instrument.symbol} position`}
                  className="w-16 shrink-0 bg-zinc-950 border border-zinc-700 rounded px-2 text-zinc-100 font-mono text-[11px] text-center focus:outline-none focus:border-emerald-500 disabled:opacity-40"
                />
                <button
                  type="button"
                  disabled={
                    submittingLabel != null ||
                    flattenHook.isPending ||
                    maxLoss.capReached ||
                    !(Number(addQty) > 0)
                  }
                  title={`Buy ${addQty || ''} ${isOption ? 'ct' : 'sh'} to add to position`}
                  onClick={triggerAdd}
                  className={`flex-1 text-[11px] font-semibold py-1.5 rounded border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    addArm.armed
                      ? 'bg-emerald-500 text-white border-emerald-300 ring-2 ring-emerald-300 animate-pulse'
                      : 'bg-emerald-950/40 hover:bg-emerald-900/50 text-emerald-300 border-emerald-800'
                  }`}
                >
                  {submittingLabel === 'Add'
                    ? 'Adding…'
                    : addArm.armed
                      ? `Confirm add ${addQty}`
                      : `Add ${addQty || ''}`.trim()}
                </button>
              </div>
              <div className="flex gap-1.5">
                {([25, 50, 75] as const).map((pct) => {
                  const q = fadeQty(pct)
                  const disabled = q < 1 || submittingLabel != null || flattenHook.isPending || maxLoss.capReached
                  const arm = fadeArms[pct]
                  const label = `Fade ${pct}%`
                  const isSubmitting = submittingLabel === label
                  return (
                    <button
                      key={pct}
                      type="button"
                      disabled={disabled}
                      title={q < 1 ? 'Too small to fade — use Flatten' : `Sell ${q} ${isOption ? 'ct' : 'sh'}`}
                      onClick={() => trigger(label, q, arm)}
                      className={`flex-1 text-[11px] font-semibold py-1.5 rounded border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        arm.armed
                          ? 'bg-amber-500 text-white border-amber-300 ring-2 ring-amber-300 animate-pulse'
                          : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border-zinc-700'
                      }`}
                    >
                      {isSubmitting
                        ? 'Sending…'
                        : arm.armed
                          ? `Confirm ${pct}%`
                          : `Fade ${pct}%`}
                    </button>
                  )
                })}
              </div>
              <button
                type="button"
                disabled={submittingLabel != null || flattenHook.isPending || maxLoss.capReached}
                onClick={() => {
                  if (flattenHook.isPending) return
                  // Quick-trade skips the confirm step; otherwise two-click since Flatten is destructive.
                  if (!quickEnabled && !sellArm.armed) {
                    sellArm.arm()
                    return
                  }
                  sellArm.clear()
                  flattenHook.flatten()
                }}
                className={`w-full text-xs font-semibold py-2 rounded transition disabled:opacity-40 disabled:cursor-not-allowed ${
                  sellArm.armed
                    ? 'bg-red-500 text-white ring-2 ring-red-300 animate-pulse'
                    : 'bg-red-600 hover:bg-red-500 text-white'
                }`}
              >
                {flattenHook.isPending
                  ? (flattenHook.progress ?? 'Flattening…')
                  : sellArm.armed
                    ? `Confirm flatten (${Math.abs(qty)})`
                    : `Flatten (${Math.abs(qty)})`}
              </button>
            </>
          ) : (
            <div className="text-[11px] text-zinc-500 italic text-center py-2 border border-dashed border-zinc-800 rounded">
              Short position — covering not supported here.
            </div>
          )}

          {groupedTx.length > 0 && (
            <TransactionsList
              transactions={groupedTx}
              isOption={isOption}
              mask={mask}
            />
          )}

          {maxLoss.capReached && (
            <div className="text-xs text-red-400 text-center py-2 font-semibold">
              {maxLoss.capReason} — no more orders until tomorrow.
            </div>
          )}
      </div>
    </div>
  )
}

interface TransactionsListProps {
  transactions: Transaction[]
  isOption: boolean
  mask: (s: string) => string
}

function TransactionsList({ transactions, isOption, mask }: TransactionsListProps) {
  return (
    <div className="border-t border-zinc-800 pt-3">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5">
        Transactions ({transactions.length})
      </div>
      <ul className="flex flex-col gap-1 font-mono text-[11px]">
        {transactions.map((t) => {
          const sideColor =
            t.side === 'BUY' ? 'text-emerald-400' : t.side === 'SELL' ? 'text-red-400' : 'text-zinc-400'
          const qty = t.quantity ?? 0
          const unit = isOption ? 'ct' : 'sh'
          const price = t.unitPrice
          const net = t.netAmount
          return (
            <li
              key={t.id}
              className="flex items-center justify-between gap-2 text-zinc-400"
            >
              <span className="text-zinc-500 shrink-0">{fmtTxTime(t.timestamp)}</span>
              <span className="flex-1 flex items-center gap-2 min-w-0">
                <span className={`font-semibold w-10 shrink-0 ${sideColor}`}>
                  {t.side ?? t.label ?? '—'}
                </span>
                <span className="text-zinc-300 truncate">
                  {qty} {unit}{' '}
                  {price != null ? `@ ${price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}` : ''}
                </span>
                {t.orderType ? (
                  <span className="text-[10px] text-zinc-500 uppercase">{t.orderType}</span>
                ) : null}
              </span>
              <span
                className={`shrink-0 ${
                  net > 0 ? 'text-emerald-400' : net < 0 ? 'text-red-400' : 'text-zinc-400'
                }`}
              >
                {mask(net.toLocaleString('en-US', { style: 'currency', currency: 'USD', signDisplay: 'always' }))}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

interface ClosedCardProps {
  row: ActiveClosedPosition
}

export function ClosedCard({ row }: ClosedCardProps) {
  const mask = useMaskMoney()
  const isAggregate = useIsAggregateScope()
  const { symbol, realizedPL, realizedPLPct, transactions, instrumentType } = row
  const accountIdsInTrade = useMemo(() => {
    const s = new Set<string>()
    for (const t of transactions) {
      if (t.accountId) s.add(t.accountId)
    }
    return [...s]
  }, [transactions])
  const isOption = instrumentType === 'OPTION'
  const positive = realizedPL >= 0
  const plClass = positive ? 'text-emerald-400' : 'text-red-400'

  const gainPositive = realizedPLPct != null && realizedPLPct >= 0
  const gainClass = gainPositive ? 'text-emerald-400' : 'text-red-400'

  // Collapse same-second fills into single rows for both the chart markers
  // and the transactions list — brokers often split a single order into many
  // micro-fills in the same second, which is noise to the user.
  const groupedTx = groupSameSecondFills(transactions)
  const chartFills = groupedTx
    .filter((t) => t.side && t.unitPrice != null && t.quantity != null)
    .map((t) => ({
      ts: Date.parse(t.timestamp),
      price: t.unitPrice as number,
      side: t.side as 'BUY' | 'SELL',
      quantity: t.quantity as number,
    }))
    .filter((f) => Number.isFinite(f.ts) && Number.isFinite(f.price))
  const canChart = chartFills.length >= 1
  const fillCount = groupedTx.length

  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-4 opacity-90">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-zinc-500" />
                <span className="font-mono font-semibold text-zinc-300">{symbol}</span>
                <span className="text-[10px] font-semibold tracking-wide text-zinc-500">
                  CLOSED · {fillCount} fill{fillCount === 1 ? '' : 's'}
                </span>
                {isAggregate && accountIdsInTrade.map((id) => (
                  <AccountChip key={id} accountId={id} />
                ))}
                <TradeLink upstreamOrderId={transactions.map((t) => t.orderId)} />
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Closed {fmtTxTime(row.closedAt)} · today's net{' '}
                <span className={plClass}>
                  {mask(
                    realizedPL.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      signDisplay: 'always',
                    }),
                  )}
                </span>
                {' '}· gain{' '}
                <span className={gainClass}>{fmtSignedPct(realizedPLPct)}</span>
              </div>
            </div>
          </div>
          <TransactionsList transactions={groupedTx} isOption={isOption} mask={mask} />
        </div>
        {canChart && (
          <div className="md:flex-1 md:min-w-0 md:pl-4 md:border-l md:border-zinc-800 flex items-center justify-center pb-5 md:pb-0">
            <div className="w-[85%]">
              <PositionPriceChart
                symbol={symbol}
                fills={chartFills}
                isUp={positive}
                isOption={isOption}
                height={120}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function fmtTxTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const today = new Date()
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  return sameDay
    ? d.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      })
    : d.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      })
}

interface PendingCardProps {
  row: ActivePendingOrder
  liveThrottled: boolean
}

export function PendingCard({ row, liveThrottled }: PendingCardProps) {
  const qc = useQueryClient()
  const mask = useMaskMoney()
  const isAggregate = useIsAggregateScope()
  const fmtMoney = (v: number | string | undefined | null) => mask(rawFmtMoney(v))
  const { order, livePrice, distance, distancePct } = row

  const [quickEnabled] = useSetting<boolean>('quick-trade-enabled', true)
  const cancelArm = useArm()
  const [submitting, setSubmitting] = useState(false)

  const cancelMutation = useMutation({
    mutationFn: () => cancelOrder(order.orderId),
    onSuccess: () => {
      setSubmitting(false)
      toast.success(`${order.instrument.symbol} · Cancel submitted`)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
    },
    onError: (err: unknown) => {
      setSubmitting(false)
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`Cancel failed — ${msg}`)
    },
  })

  function handleCancel() {
    if (submitting) return
    if (!quickEnabled && !cancelArm.armed) {
      cancelArm.arm()
      return
    }
    cancelArm.clear()
    setSubmitting(true)
    cancelMutation.mutate()
  }

  const sideColor = order.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'
  const referencePrice = order.limitPrice ?? order.stopPrice
  const distanceClass =
    distance == null
      ? 'text-zinc-500'
      : distance >= 0
        ? 'text-emerald-400'
        : 'text-red-400'

  const dotColor = liveThrottled ? 'bg-amber-400' : 'bg-emerald-400'
  const dotAnim = liveThrottled ? '' : 'animate-pulse'

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor} ${dotAnim}`} />
            <span className="font-mono font-semibold text-zinc-100">
              {order.instrument.symbol}
            </span>
            <span className={`text-[10px] font-semibold tracking-wide ${sideColor}`}>
              {order.side} {order.type}
            </span>
            {isAggregate && order.accountId && (
              <AccountChip accountId={order.accountId} />
            )}
            <TradeLink upstreamOrderId={order.orderId} />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1 truncate">
            {order.quantity ?? '—'} @ {fmtMoney(referencePrice)} · placed{' '}
            {fmtTimeOfDay(order.createdAt)}
            {' '}
            <span className="text-zinc-600">({elapsedSince(order.createdAt)})</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-xl text-zinc-100">
            {livePrice != null ? fmtMoney(livePrice) : '—'}
          </div>
          <div className={`font-mono text-sm ${distanceClass}`}>
            {distance != null ? fmtSignedMoney(distance) : '—'}{' '}
            <span className="text-xs">
              ({distance != null ? fmtSignedPct(distancePct) : '—'})
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-[11px] uppercase tracking-wide">
        <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
          PENDING · {order.status}
        </span>
        <span className="text-zinc-500">TIF {order.expiration?.timeInForce ?? '—'}</span>
      </div>

      <button
        type="button"
        disabled={submitting}
        onClick={handleCancel}
        className={`w-full text-xs font-semibold py-2 rounded transition disabled:opacity-40 disabled:cursor-not-allowed ${
          cancelArm.armed
            ? 'bg-amber-500 text-white ring-2 ring-amber-300 animate-pulse'
            : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700'
        }`}
      >
        {submitting ? 'Cancelling…' : cancelArm.armed ? 'Confirm cancel' : 'Cancel'}
      </button>
    </div>
  )
}
